import * as React from "react";

/**
 * Public surface returned by {@link useEditableField}.
 *
 * - ``editable`` — whether the editor is mounted.
 * - ``currentValue`` — the last-saved value the display branch should render.
 * - ``beginEdit`` — switch into edit mode; clears any pending cancel flag so
 *   a follow-up blur after an Escape cancellation doesn't re-enter editing.
 * - ``cancel`` — exit edit mode without saving. Sets the cancel flag so the
 *   synchronous blur that fires when the editor unmounts is suppressed.
 * - ``save`` — idempotent commit. Trims the candidate, short-circuits if the
 *   trimmed value matches ``currentValue``, calls ``onSave``, and on success
 *   updates the cached display value and exits edit mode. If ``onSave``
 *   rejects, editing stays open so the parent can surface the mutation
 *   error inline.
 */
export interface EditableField {
  editable: boolean;
  currentValue: string;
  beginEdit: () => void;
  cancel: () => void;
  save: (value: string) => Promise<void>;
}

interface UseEditableFieldOptions {
  /**
   * Initial committed value. Re-syncs into local state whenever this prop
   * changes (e.g. when the parent re-fetches a fresh record).
   */
  initial: string;
  /**
   * Persists the trimmed candidate. Resolves on success; rejects to keep
   * the editor open. The hook does not handle rejections — callers wire the
   * mutation error to their own inline surface.
   */
  onSave: (trimmed: string) => Promise<unknown>;
}

/**
 * Shared save/cancel state machine for click-to-edit inline fields.
 *
 * Encapsulates the four pieces that previously duplicated across
 * ``EditableSessionName`` and ``EditableSessionDescription``:
 *
 * 1. ``editable`` toggle between display and editor.
 * 2. ``savingRef`` re-entrancy guard so a fast blur+Enter combo doesn't
 *    double-fire the mutation.
 * 3. ``cancelledRef`` blur-after-Escape suppression — Escape unmounts the
 *    input which schedules a synchronous blur; without this flag the blur
 *    handler would treat Escape as a save.
 * 4. Idempotent trim+equality short-circuit so a no-op edit (open → close)
 *    does not bump ``modified`` server-side.
 *
 * The hook does not own any DOM or rendering; the consumer supplies both the
 * display and editor branches and forwards events to ``save``/``cancel``.
 */
export function useEditableField({
  initial,
  onSave,
}: UseEditableFieldOptions): EditableField {
  const [editable, setEditable] = React.useState(false);
  const [currentValue, setCurrentValue] = React.useState(initial);
  const savingRef = React.useRef(false);
  const cancelledRef = React.useRef(false);

  // Re-sync the cached display value when the parent supplies a fresh
  // ``initial`` (e.g. the record refetched). Dependency on
  // ``initial`` alone — React only invokes the effect when the value
  // actually changes.
  React.useEffect(() => {
    setCurrentValue(initial);
  }, [initial]);

  const beginEdit = React.useCallback(() => {
    cancelledRef.current = false;
    setEditable(true);
  }, []);

  const cancel = React.useCallback(() => {
    cancelledRef.current = true;
    setEditable(false);
  }, []);

  const save = React.useCallback(
    async (value: string) => {
      if (savingRef.current || cancelledRef.current) {
        return;
      }
      savingRef.current = true;
      const trimmed = value.trim();
      if (trimmed === currentValue) {
        setEditable(false);
        savingRef.current = false;
        return;
      }
      try {
        await onSave(trimmed);
        setCurrentValue(trimmed);
        setEditable(false);
      } catch {
        // Caller surfaces the rejection via its mutation's `error`. Stay in
        // edit mode so the user can retry.
      } finally {
        savingRef.current = false;
      }
    },
    [currentValue, onSave],
  );

  return { editable, currentValue, beginEdit, cancel, save };
}
